import assert from "assert";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { SheetGeometry, SheetSVGGeometry, StyledSheetTextGeometry } from "building-types/views/sheetGeometryTypes";
import { SheetGeometryType } from "building-types/views/sheetGeometryTypes";
import { SVG_UNITS_TO_INCHES } from "common/constants/pdfSettings";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { radianToDegree } from "common/utils/math";
import { nominalNumberNew, numMax } from "common/utils/nominalNumbers";
import { objectMergeCascade } from "common/utils/objectMergeCascade";
import { DIST_EPSILON } from "geom/core";
import type { Mat3 } from "geom/mat3";
import {
  mat3Copy,
  mat3Determinant,
  mat3Dot,
  mat3DotNoTranslate,
  mat3ExtractRotationAngleFrom,
  mat3ExtractScaleVectorFrom,
  mat3Mult,
  mat3Rotate,
  mat3Scale,
} from "geom/mat3";
import { polyLine2TransformCopy } from "geom/polyLine2";
import { PDFTextMetricsProvider } from "pdf/pdfTextMetricsProvider";
import { modelSpaceToPaperSpaceTransformWithPlanRotation } from "../pdf/drawingLayout";
import type { DrawingLayout } from "./layout";
import styles from "./planStyles";
import { normalizeTextBox } from "./primitives/textPrimitive";

export interface TransformWithComponents {
  transform: Mat3;
  transformScale: [number, number];
  transformRotation: number; // radians
}

const UNIFORM_SCALE_RELATIVE_TOLERANCE = 0.0001;

export function isUniformScale(sx: number, sy: number, relativeTolerance = UNIFORM_SCALE_RELATIVE_TOLERANCE): boolean {
  const absSx = Math.abs(sx);
  const absSy = Math.abs(sy);
  return Math.abs(absSx - absSy) < (absSx + absSy + 0.00000001) * relativeTolerance;
}

export function mat3TransformWithComponentsUnchecked(transform: Mat3): TransformWithComponents {
  const transformScale = mat3ExtractScaleVectorFrom(transform);
  const transformRotation = mat3ExtractRotationAngleFrom(transform, transformScale);

  return {
    transform,
    transformScale: [transformScale[0], transformScale[1]],
    transformRotation: transformRotation ?? 0,
  };
}

export function mat3TransformWithComponents(transform: Mat3): TransformWithComponents {
  const result = mat3TransformWithComponentsUnchecked(transform);
  assert(isUniformScale(result.transformScale[0], result.transformScale[1]));
  return result;
}

// Applies only a page transform's rotation and mirroring to a direction vector, normalizing out the
// uniform scale. Text offsets must follow the page's orientation without being re-scaled.
function transformDirection(
  { transform, transformScale }: TransformWithComponents,
  dx: SheetInches | undefined,
  dy: SheetInches | undefined,
): [SheetInches | undefined, SheetInches | undefined] {
  if (dx === undefined && dy === undefined) {
    return [dx, dy];
  }

  const magnitude = Math.abs(transformScale[0]);
  if (magnitude < DIST_EPSILON) {
    return [dx, dy];
  }

  const [rx, ry] = mat3DotNoTranslate(transform, dx ?? 0, dy ?? 0);
  return [nominalNumberNew<SheetInches>(rx / magnitude), nominalNumberNew<SheetInches>(ry / magnitude)];
}

/**
 * The goal of this function is to ultimately be the source of truth for transformations
 * being applied to plan geometry before they are exported to a page.
 *
 * We ultimately want this to be the ONLY place that we are applying page related tranforms
 */
export function sheetGeometryTransform<T extends SheetGeometry>(
  geom: T,
  transformWithComponents: TransformWithComponents,
  normalizeTextRotation = true,
): T {
  // individual transform components = these should probably be stored adjacent to the tranform matrix
  // so that they can accessed if needed
  const { transform, transformScale, transformRotation } = transformWithComponents;
  // const transformTranslation = not used currently

  switch (geom.type) {
    case SheetGeometryType.TEXT: {
      const { rotationBasis } = geom;
      const [x, y] = mat3Dot(transform, geom.x, geom.y);
      // A direction, not a point: rotation and mirror only. Scale is excluded because the editor
      // viewer scales before transforming while the viewport and plan-set renderers transform first.
      const [offsetX, offsetY] = transformDirection(transformWithComponents, geom.offsetX, geom.offsetY);
      if (normalizeTextRotation) {
        return normalizeTextBox(
          {
            ...geom,
            x,
            y,
            offsetX,
            offsetY,
          },
          rotationBasis === "SHEET" ? 0 : transformRotation,
        ) as T;
      }
      const { rotation = 0 } = geom;
      return {
        ...geom,
        x,
        y,
        offsetX,
        offsetY,
        rotation: rotation + transformRotation,
      };
    }
    case SheetGeometryType.ASSET: {
      // Assets apply transformations to geometry at render time.
      if (geom.transform) {
        return {
          ...geom,
          transform: mat3Mult(transform, geom.transform),
        };
      } else {
        return {
          ...geom,
          transform: mat3Copy(transform),
        };
      }
    }
    case SheetGeometryType.SVG: {
      const [x, y] = mat3Dot(transform, geom.x, geom.y);
      let path = geom.path;
      if (transformRotation !== 0) {
        path = `
          <g
            transform="rotate(${radianToDegree(transformRotation)})"
          >${geom.path}</g>
        `;
      }
      return {
        ...geom,
        path,
        x,
        y,
        scale: geom.scale * (geom.inPlanScale ? transformScale[0] : 1),
      };
    }
    case SheetGeometryType.POLYLINE: {
      return {
        ...geom,
        paths: geom.paths.map((p) => polyLine2TransformCopy(p, transform)),
      };
    }
    case SheetGeometryType.POLYGON: {
      return {
        ...geom,
        paths: geom.paths.map((p) => p.map((l) => polyLine2TransformCopy(l, transform))),
      };
    }

    case SheetGeometryType.CIRCLE: {
      const [x, y] = mat3Dot(transform, geom.x, geom.y);
      const radius = geom.radius * transformScale[0];
      return {
        ...geom,
        x,
        y,
        radius,
      };
    }
    case SheetGeometryType.ARC: {
      const xRadius0 = geom.xRadius;
      const yRadius0 = geom.yRadius ?? geom.xRadius;
      const [x, y] = mat3Dot(transform, geom.x, geom.y);
      const xRadius = xRadius0 * Math.abs(transformScale[0]);
      const yRadius = yRadius0 * Math.abs(transformScale[1]);
      // Circular: recover angles from endpoints so X-mirror is not treated as Y-flip.
      if (Math.abs(xRadius0 - yRadius0) <= DIST_EPSILON) {
        const [startX, startY] = mat3Dot(
          transform,
          geom.x + xRadius0 * Math.cos(geom.startRad),
          geom.y + yRadius0 * Math.sin(geom.startRad),
        );
        const reflected = mat3Determinant(transform) < 0;
        const startRad = Math.atan2(startY - y, startX - x);
        const sweep = geom.endRad - geom.startRad;
        return {
          ...geom,
          x,
          y,
          xRadius,
          yRadius,
          startRad,
          endRad: startRad + (reflected ? -sweep : sweep),
          clockwise: reflected ? !geom.clockwise : !!geom.clockwise,
        };
      }
      const startRad = (transformScale[1] < 0 ? -geom.startRad : geom.startRad) + transformRotation;
      const endRad = (transformScale[1] < 0 ? -geom.endRad : geom.endRad) + transformRotation;
      const clockwise = transformScale[1] < 0 ? !geom.clockwise : geom.clockwise;
      return {
        ...geom,
        x,
        y,
        xRadius,
        yRadius,
        startRad,
        endRad,
        clockwise,
      };
    }
    default: {
      assertUnreachable(geom);
    }
  }
}

export function sheetGeometryYFlipTransform(scaleFactor = 1, rotationRads = 0): TransformWithComponents {
  const transform = mat3Mult(mat3Rotate(rotationRads), mat3Scale(scaleFactor, -scaleFactor));

  return {
    transform,
    transformScale: [scaleFactor, -scaleFactor],
    transformRotation: rotationRads,
  };
}

export function sheetGeometryDrawingLayoutTransform(
  layout: DrawingLayout,
  planRotationRads: number = 0,
): TransformWithComponents {
  const transform = modelSpaceToPaperSpaceTransformWithPlanRotation(layout, planRotationRads);

  // individual transform components = these should probably be stored adjacent to the tranform matrix
  // so that they can accessed if needed
  const transformRotation = planRotationRads;
  const transformScale: [number, number] = [layout?.scaleFactor ?? 1, -(layout?.scaleFactor ?? 1)];
  return {
    transform,
    transformScale,
    transformRotation,
  };
}

export function sheetSVGGeometryScale(geom: SheetSVGGeometry): number {
  return (geom.inPlanScale ? 1 : SVG_UNITS_TO_INCHES) * geom.scale;
}

export function isEditingAnnotation(
  geometry: SheetGeometry,
  editingEntityHandle: DocEntityHandle | undefined,
): boolean {
  if (geometry.type !== SheetGeometryType.TEXT) {
    return false;
  }

  if (!geometry.handle || !editingEntityHandle) {
    return false;
  }

  if (geometry.handle.type === "ANNOTATION" && editingEntityHandle.type === "ANNOTATION") {
    return geometry.handle.id === editingEntityHandle.id;
  }

  return false;
}

export function maybeOverrideEditingAnnotation(
  geometry: SheetGeometry,
  editingEntityHandle: DocEntityHandle | undefined,
  editingEntityProps: Record<string, any> | undefined,
) {
  if (isEditingAnnotation(geometry, editingEntityHandle)) {
    assertIsNotUndefined(editingEntityProps);
    const overrideGeom = objectMergeCascade([styles.text, geometry, editingEntityProps]) as StyledSheetTextGeometry;
    const measuredHeight = PDFTextMetricsProvider.instance.heightOfString(overrideGeom);
    const boxHeight = overrideGeom.boxHeight ? numMax(measuredHeight, overrideGeom.boxHeight) : measuredHeight;
    return { ...overrideGeom, boxHeight };
  }
  return geometry;
}
